import { useLang } from "@/i18n/LangContext";
import { useWishlist } from "@/hooks/useWishlist";
import type { AppDetail } from "@/types/api";

interface GameHeaderCardProps {
  app: AppDetail;
  freeGameInfo?: { store: string; endDate?: string | null } | null;
}

export default function GameHeaderCard({ app, freeGameInfo }: GameHeaderCardProps) {
  const { t } = useLang();
  const { has, toggle } = useWishlist();
  const isWished = has(app.steam_appid);

  const price = app.price_overview;

  return (
    <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-4 sm:p-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-3">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {app.name}
            </h1>
            <button
              onClick={() => toggle(app.steam_appid)}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                isWished
                  ? "bg-red-50 dark:bg-red-500/10 text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20"
                  : "bg-inner-bg dark:bg-inner-bg text-gray-400 hover:text-red-500 hover:bg-red-50"
              }`}
              title={isWished ? "관심 목록에서 제거" : "관심 목록에 추가"}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={isWished ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-muted mb-4">
            {app.developers?.length > 0 && (
              <span>{t.gameDetail.developer}: <span className="text-gray-700 dark:text-foreground">{app.developers.join(", ")}</span></span>
            )}
            {app.publishers?.length > 0 && (
              <>
                <span className="text-gray-300 dark:text-[#2a3f5f]">|</span>
                <span>{t.gameDetail.publisher}: <span className="text-gray-700 dark:text-foreground">{app.publishers.join(", ")}</span></span>
              </>
            )}
            {app.release_date && (
              <>
                <span className="text-gray-300 dark:text-[#2a3f5f]">|</span>
                <span>{t.gameDetail.release}: <span className="text-gray-700 dark:text-foreground">{app.release_date.date}</span></span>
              </>
            )}
          </div>
          {app.genres && (
            <div className="flex flex-wrap gap-1.5">
              {app.genres.map((g) => (
                <span
                  key={g.id}
                  className="text-xs bg-hover-bg dark:bg-background text-gray-600 dark:text-muted px-2.5 py-1 rounded-lg font-medium"
                >
                  {g.description}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* 가격 카드 */}
        <div className="shrink-0 flex flex-row sm:flex-col items-center sm:items-end gap-3">
          {app.is_free ? (
            <span className="text-lg font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-2 rounded-xl border border-emerald-200 dark:border-emerald-500/30">{t.gameDetail.free}</span>
          ) : price ? (
            <div className="flex items-center gap-3 bg-inner-bg dark:bg-inner-bg rounded-xl px-5 py-3 border border-gray-200 dark:border-card-border">
              {price.discount_percent > 0 && (
                <span className="bg-emerald-500 text-white text-lg font-bold px-3 py-1 rounded-lg">
                  -{price.discount_percent}%
                </span>
              )}
              <div className="flex flex-col items-end">
                {price.discount_percent > 0 && (
                  <span className="text-sm text-gray-400 dark:text-muted line-through">
                    {price.initial_formatted}
                  </span>
                )}
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  {price.final_formatted}
                </span>
              </div>
            </div>
          ) : null}
          {freeGameInfo && !app.is_free && (
            <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-xl px-4 py-2">
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{freeGameInfo.store}</span>
              <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">{t.common.free}</span>
            </div>
          )}
          {app.metacritic && (
            <div className="flex items-center gap-2">
              <span
                className={`text-sm font-bold px-2.5 py-1 rounded-lg ${
                  app.metacritic.score >= 75
                    ? "bg-emerald-500 text-white"
                    : app.metacritic.score >= 50
                      ? "bg-amber-400 text-gray-900"
                      : "bg-red-500 text-white"
                }`}
              >
                {app.metacritic.score}
              </span>
              <span className="text-xs text-gray-500 dark:text-muted">{t.gameDetail.metaScore}</span>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
